import React, { useEffect, useState, useCallback } from 'react';
import { api } from '@/contexts/AuthContext';
import { useDropzone } from 'react-dropzone';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import { Upload, FileSpreadsheet, Trash2, Zap, CheckCircle, Clock, AlertCircle, RefreshCw, User, Phone } from 'lucide-react';
import { toast } from 'sonner';

export default function ImportsPage() {
  const [imports, setImports] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [enriching, setEnriching] = useState({});

  const loadImports = useCallback(async () => {
    try {
      const { data } = await api.get('/imports');
      setImports(data);
      // Check enrichment progress
      const inProgress = data.filter(i => i.statut === 'en_cours');
      const newEnriching = {};
      inProgress.forEach(i => { newEnriching[i.id] = true; });
      setEnriching(prev => ({ ...prev, ...newEnriching }));
    } catch {}
  }, []);

  useEffect(() => { loadImports(); }, [loadImports]);

  // Poll for enrichment progress
  useEffect(() => {
    const ids = Object.keys(enriching).filter(k => enriching[k]);
    if (ids.length === 0) return;
    const interval = setInterval(async () => {
      for (const id of ids) {
        try {
          const { data } = await api.get(`/imports/${id}`);
          setImports(prev => prev.map(i => i.id === id ? { ...i, ...data } : i));
          if (data.statut === 'termine' || data.statut === 'erreur') {
            setEnriching(prev => ({ ...prev, [id]: false }));
            toast.success(`Enrichissement terminé: ${data.nom_fichier}`);
          }
        } catch {}
      }
    }, 2000);
    return () => clearInterval(interval);
  }, [enriching]);

  const onDrop = useCallback(async (acceptedFiles) => {
    const file = acceptedFiles[0];
    if (!file) return;
    setUploading(true);
    const formData = new FormData();
    formData.append('file', file);
    try {
      const { data } = await api.post('/imports/upload', formData, { headers: { 'Content-Type': 'multipart/form-data' } });
      const parts = [`${data.nb_prospects} prospects`, `${data.nb_doublons} doublons`];
      if (data.nb_wasabi_exclus > 0) parts.push(`🛡 ${data.nb_wasabi_exclus} clients Wasabi exclus`);
      if (data.nb_hanagroup_exclus > 0) parts.push(`⚔️ ${data.nb_hanagroup_exclus} positions Hanagroup exclues`);
      if (data.nb_emails_generes > 0) parts.push(`📧 ${data.nb_emails_generes} emails auto-générés`);
      if (data.nb_patterns_appris > 0) parts.push(`🧠 ${data.nb_patterns_appris} nouveau${data.nb_patterns_appris > 1 ? 'x' : ''} pattern${data.nb_patterns_appris > 1 ? 's' : ''} appris`);
      toast.success(`Import réussi: ${parts.join(' • ')}`, { duration: 9000 });
      loadImports();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erreur d'import");
    }
    setUploading(false);
  }, [loadImports]);

  const downloadTemplate = async () => {
    try {
      const res = await api.get('/imports/template/download', { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement('a');
      a.href = url;
      a.download = 'modele_import_wasabi_prospecttel.xlsx';
      a.click();
      window.URL.revokeObjectURL(url);
      toast.success('Modèle Excel téléchargé');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erreur téléchargement');
    }
  };

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop, accept: { 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': ['.xlsx'] }, maxFiles: 1
  });

  const startEnrichment = async (importId, force = false) => {
    try {
      await api.post(`/imports/${importId}/enrich?force=${force}`);
      setEnriching(prev => ({ ...prev, [importId]: true }));
      toast.success(force ? 'Ré-enrichissement démarré (SerpAPI)' : 'Enrichissement démarré');
      loadImports();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erreur');
    }
  };

  const startDirigeantEnrichment = async (importId) => {
    try {
      await api.post(`/imports/${importId}/enrich-dirigeants`);
      setEnriching(prev => ({ ...prev, [importId]: true }));
      toast.success('Recherche des dirigeants démarrée (data.gouv + SerpAPI)');
      loadImports();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erreur');
    }
  };

  const retryAllDirigeants = async () => {
    if (!window.confirm("Lancer une re-recherche Pappers sur tous les prospects sans dirigeant ? Cela prendra quelques minutes.")) return;
    try {
      await api.post('/enrichment/retry-dirigeants');
      toast.success('Recherche globale des dirigeants lancée.');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erreur');
    }
  };

  const retryAllPhones = async () => {
    if (!window.confirm("Lancer une re-recherche SerpAPI sur tous les prospects sans téléphone ? Cela prendra quelques minutes.")) return;
    try {
      await api.post('/enrichment/retry-phones');
      toast.success('Recherche globale des téléphones lancée.');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erreur');
    }
  };

  const resetImportStatus = async (importId) => {
    try {
      await api.post(`/imports/${importId}/reset-status`);
      toast.success('Statut réinitialisé');
      loadImports();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erreur');
    }
  };

  const deleteImport = async (importId) => {
    if (!window.confirm('Supprimer cet import et tous ses prospects ?')) return;
    try {
      await api.delete(`/imports/${importId}`);
      toast.success('Import supprimé');
      loadImports();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erreur');
    }
  };

  const statusIcon = (statut) => {
    switch (statut) {
      case 'termine': return <CheckCircle className="w-4 h-4 text-[#10B981]" />;
      case 'en_cours': return <RefreshCw className="w-4 h-4 text-[#0055FF] animate-spin" />;
      case 'erreur': return <AlertCircle className="w-4 h-4 text-[#EF4444]" />;
      default: return <Clock className="w-4 h-4 text-[#71717A]" />;
    }
  };

  return (
    <div className="space-y-6 animate-fade-in" data-testid="imports-page">
      <div className="flex items-start justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold tracking-tight" style={{ fontFamily: 'var(--font-heading)' }}>Import Excel</h1>
          <p className="text-sm text-[#71717A] mt-1">Importez vos fichiers de prospects magasins</p>
        </div>
        <div className="flex flex-wrap gap-2">
          <Button
            onClick={retryAllPhones}
            variant="outline"
            size="sm"
            className="h-9 gap-1.5 border-[#0055FF] text-[#0055FF] hover:bg-[#EFF6FF]"
            data-testid="retry-all-phones-btn"
          >
            <Phone className="w-3.5 h-3.5" /> Re-rechercher téléphones manquants
          </Button>
          <Button
            onClick={retryAllDirigeants}
            variant="outline"
            size="sm"
            className="h-9 gap-1.5 border-[#F97316] text-[#F97316] hover:bg-[#FFF7ED]"
            data-testid="retry-all-dirigeants-btn"
          >
            <User className="w-3.5 h-3.5" /> Re-rechercher dirigeants manquants
          </Button>
        </div>
      </div>

      {/* Consignes d'import + téléchargement modèle */}
      <Card className="border-2 border-[#F97316]/20 shadow-sm bg-gradient-to-br from-[#FFF7ED] to-white">
        <CardContent className="p-6 space-y-4">
          <div className="flex items-start justify-between gap-3 flex-wrap">
            <div className="flex items-start gap-2.5">
              <div className="w-9 h-9 rounded-lg bg-[#F97316]/15 flex items-center justify-center flex-shrink-0">
                <FileSpreadsheet className="w-5 h-5 text-[#F97316]" />
              </div>
              <div>
                <h3 className="text-base font-semibold">Consignes d'import · Trame à suivre</h3>
                <p className="text-xs text-[#71717A] mt-0.5">
                  Colonnes attendues dans le fichier Excel. L'ordre est flexible, les noms sont détectés automatiquement.
                </p>
              </div>
            </div>
            <Button size="sm" onClick={downloadTemplate}
              className="h-9 bg-[#F97316] hover:bg-[#EA580C] text-white"
              data-testid="download-template-btn">
              <FileSpreadsheet className="w-3.5 h-3.5 mr-1.5" /> Télécharger le modèle Excel
            </Button>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
            {[
              { label: 'Enseigne', required: true, example: 'Intermarché', note: 'Leclerc, Super U, Carrefour, Hyper U…' },
              { label: 'Nom Magasin', required: true, example: 'Intermarché Toulouse Centre', note: 'Nom complet avec ville/quartier' },
              { label: 'Adresse (ou Rue)', required: true, example: '30 Chemin Ferro-Lebres', note: 'Numéro + rue' },
              { label: 'Code Postal (ou CP)', required: true, example: '31000', note: '5 chiffres' },
              { label: 'Ville (ou City)', required: true, example: 'Toulouse', note: 'Ville du magasin' },
              { label: 'Marque', required: false, example: 'Kellidely', note: 'Nom commercial / adhérent (facultatif)' },
              { label: 'Prénom Dirigeant', required: false, example: 'Stéphane', note: 'Si vide → auto via Pappers/Google' },
              { label: 'Nom Dirigeant', required: false, example: 'Pouyet', note: 'Si vide → auto via Pappers/Google' },
              { label: 'Adresse Mail', required: false, example: 'prenom.nom@cooperative-u.fr', note: 'Si vide → auto-généré selon l\'enseigne' },
              { label: 'CA Alimentaire Estimatif', required: false, example: '12000000 (ou 12M€)', note: 'CA annuel en € — accepte 12M, 12 000 000, 12M€...' },
            ].map((c) => (
              <div key={c.label} className="flex items-center gap-3 p-2.5 rounded-lg bg-white border border-[#E5E7EB]">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-1.5">
                    <span className="text-xs font-semibold text-[#18181B]">{c.label}</span>
                    {c.required ? (
                      <Badge className="bg-[#EF4444]/10 text-[#DC2626] border-none h-4 text-[9px] px-1.5">Obligatoire</Badge>
                    ) : (
                      <Badge className="bg-[#F4F4F5] text-[#71717A] border-none h-4 text-[9px] px-1.5">Optionnel</Badge>
                    )}
                  </div>
                  <p className="text-[11px] text-[#71717A] mt-0.5">{c.note}</p>
                </div>
                <code className="text-[11px] font-mono text-[#0055FF] bg-[#EFF6FF] px-2 py-0.5 rounded whitespace-nowrap">{c.example}</code>
              </div>
            ))}
          </div>

          <div className="p-3 rounded-lg border border-dashed border-[#F97316]/30 bg-white text-[11px] text-[#71717A] space-y-1">
            <p><strong className="text-[#18181B]">🛡️ Règles auto-appliquées à l'import :</strong></p>
            <p>• Les magasins déjà <strong>clients Wasabi</strong> sont automatiquement écartés</p>
            <p>• Les positions occupées par <strong>HANAGROUP</strong> (concurrent) sont automatiquement écartées</p>
            <p>• Les doublons (enseigne + nom_magasin + ville) sont ignorés</p>
          </div>
        </CardContent>
      </Card>

      {/* Upload Zone */}
      <Card className="border border-[#E5E7EB] shadow-sm">
        <CardContent className="p-6">
          <div {...getRootProps()}
            className={`upload-zone rounded-xl p-12 flex flex-col items-center justify-center cursor-pointer ${isDragActive ? 'active' : ''}`}
            data-testid="import-excel-dropzone">
            <input {...getInputProps()} data-testid="file-input" />
            <div className="w-14 h-14 rounded-2xl bg-[#0055FF]/5 flex items-center justify-center mb-4">
              <Upload className="w-6 h-6 text-[#0055FF]" />
            </div>
            {uploading ? (
              <p className="text-sm font-medium text-[#0055FF]">Upload en cours...</p>
            ) : isDragActive ? (
              <p className="text-sm font-medium text-[#0055FF]">Déposez le fichier ici</p>
            ) : (
              <>
                <p className="text-sm font-medium">Glissez-déposez un fichier Excel ici</p>
                <p className="text-xs text-[#71717A] mt-1">ou cliquez pour sélectionner (.xlsx)</p>
              </>
            )}
            <p className="text-xs text-[#71717A] mt-3">Colonnes attendues: Marque, Enseigne, Nom du magasin, Rue, Code postal, Ville</p>
          </div>
        </CardContent>
      </Card>

      {/* Imports List */}
      <Card className="border border-[#E5E7EB] shadow-sm">
        <CardContent className="p-6">
          <h3 className="text-sm font-semibold mb-4 flex items-center gap-2">
            <FileSpreadsheet className="w-4 h-4 text-[#0055FF]" />
            Historique des imports
          </h3>
          {imports.length === 0 ? (
            <p className="text-sm text-[#71717A] text-center py-8">Aucun import pour le moment</p>
          ) : (
            <div className="space-y-3">
              {imports.map(imp => (
                <div key={imp.id} className="flex items-center gap-4 p-4 bg-[#F9FAFB] rounded-lg border border-[#E5E7EB]/50" data-testid={`import-${imp.id}`}>
                  <div className="flex-shrink-0">{statusIcon(imp.statut)}</div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium truncate">{imp.nom_fichier}</p>
                    <p className="text-xs text-[#71717A]">{imp.nb_lignes} lignes &bull; {new Date(imp.date_import).toLocaleDateString('fr-FR', { day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' })}</p>
                    {imp.statut === 'en_cours' && (
                      <div className="mt-2">
                        <Progress value={imp.nb_lignes > 0 ? ((imp.nb_traites || 0) / (imp.nb_total_enrichissement || imp.nb_lignes)) * 100 : 0} className="h-2" />
                        <p className="text-xs text-[#0055FF] mt-1">{imp.nb_traites || 0} / {imp.nb_total_enrichissement || imp.nb_lignes} lignes traitées</p>
                      </div>
                    )}
                  </div>
                  <div className="flex items-center gap-2">
                    <Badge variant="secondary" className={`text-xs status-${imp.statut}`}>
                      {imp.statut === 'termine' ? 'Terminé' : imp.statut === 'en_cours' ? 'En cours' : imp.statut === 'importe' ? 'Importé' : imp.statut === 'interrompu' ? 'Interrompu' : imp.statut === 'erreur' ? 'Erreur' : imp.statut}
                    </Badge>
                    {(imp.statut === 'en_cours' || imp.statut === 'interrompu' || imp.statut === 'erreur') && (
                      <Button size="sm" variant="outline" onClick={() => resetImportStatus(imp.id)} data-testid={`reset-${imp.id}`}
                        className="h-8 text-xs gap-1 border-[#F59E0B] text-[#F59E0B] hover:bg-[#FFFBEB]"
                        title="Réinitialiser le statut (utile si l'enrichissement a été interrompu)">
                        <RefreshCw className="w-3.5 h-3.5" /> Reset
                      </Button>
                    )}
                    {(imp.statut === 'importe' || imp.statut === 'termine' || imp.statut === 'interrompu' || imp.statut === 'erreur') && (
                      <>
                        <Button size="sm" variant="outline" onClick={() => startEnrichment(imp.id)} data-testid={`enrich-${imp.id}`}
                          className="h-8 text-xs gap-1 border-[#0055FF] text-[#0055FF] hover:bg-[#0055FF]/5">
                          <Zap className="w-3.5 h-3.5" />Enrichir
                        </Button>
                        {imp.statut === 'termine' && (
                          <Button size="sm" variant="outline" onClick={() => startEnrichment(imp.id, true)} data-testid={`re-enrich-${imp.id}`}
                            className="h-8 text-xs gap-1 border-[#F97316] text-[#F97316] hover:bg-[#F97316]/5">
                            <RefreshCw className="w-3.5 h-3.5" />Ré-enrichir
                          </Button>
                        )}
                        {imp.statut === 'termine' && (
                          <Button size="sm" variant="outline" onClick={() => startDirigeantEnrichment(imp.id)} data-testid={`dirigeants-${imp.id}`}
                            className="h-8 text-xs gap-1 border-[#8B5CF6] text-[#8B5CF6] hover:bg-[#8B5CF6]/5">
                            <User className="w-3.5 h-3.5" />Dirigeants
                          </Button>
                        )}
                      </>
                    )}
                    <Button size="sm" variant="ghost" onClick={() => deleteImport(imp.id)} data-testid={`delete-import-${imp.id}`}
                      className="h-8 w-8 p-0 text-[#71717A] hover:text-[#EF4444]">
                      <Trash2 className="w-3.5 h-3.5" />
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
